import { useEffect, useRef } from "react";

const images = {
  hero: "/assets/images/blog/hero-mother-exhausted.jpeg",
  caregiver: "/assets/images/blog/mother-comforting-child.png",
  family: "/assets/images/blog/family-together.png",
  support: "/assets/images/blog/hands-joined-support.jpeg",
  hope: "/assets/images/blog/family-together.png",
  community: "/assets/images/blog/counselling-session.jpeg",
};

const pullQuotes = [
  "Behind every therapy session is a caregiver — and that circle can grow tired.",
  "If caregivers are emotionally depleted, progress cannot be sustained.",
  "When we strengthen caregivers, we strengthen children. When we strengthen families, we prevent isolation.",
];

export default function CaregiverSupportBlogPost() {
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRefs.current.forEach((ref) => ref && observer.observe(ref));
    return () => observer.disconnect();
  }, []);

  const addRef = (el) => {
    if (el && !sectionRefs.current.includes(el)) sectionRefs.current.push(el);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Inter:wght@300;400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        body {
          background: #FBF8F4;
          color: #2C2C2C;
          font-family: 'Inter', sans-serif;
          line-height: 1.8;
        }

        .blog-wrap {
          background: #FBF8F4;
          min-height: 100vh;
        }

        /* ── Hero ── */
        .hero {
          position: relative;
          height: 78vh;
          min-height: 480px;
          overflow: hidden;
          display: flex;
          align-items: flex-end;
        }

        .hero img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center 35%;
          filter: brightness(0.45) saturate(0.85);
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(20, 28, 32, 0.92) 0%, rgba(20,28,32,0.3) 55%, transparent 80%);
        }

        .hero-content {
          position: relative;
          z-index: 2;
          padding: 0 clamp(1.5rem, 8vw, 9rem) 3.5rem;
          max-width: 880px;
        }

        .hero-tag {
          display: inline-block;
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #C9A876;
          border: 1px solid #C9A876;
          padding: 6px 14px;
          border-radius: 2px;
          margin-bottom: 1.2rem;
        }

        .hero h1 {
          font-family: 'Playfair Display', serif;
          font-size: clamp(1.8rem, 4.6vw, 3.4rem);
          font-weight: 700;
          color: #fff;
          line-height: 1.22;
          margin-bottom: 1.1rem;
          max-width: 760px;
        }

        .hero-meta {
          font-size: 0.85rem;
          color: rgba(255,255,255,0.6);
          letter-spacing: 0.04em;
        }

        /* ── Layout ── */
        .article-layout {
          display: grid;
          grid-template-columns: 1fr min(680px, 90%) 1fr;
        }

        .article-layout > * { grid-column: 2; }
        .full-bleed { grid-column: 1 / -1 !important; }

        /* ── Intro / dropcap ── */
        .intro-section { padding: 3.5rem 0 0.5rem; }

        .intro-section p:first-of-type::first-letter {
          font-family: 'Playfair Display', serif;
          font-size: 4.4rem;
          font-weight: 700;
          color: #5C6B5D;
          float: left;
          line-height: 0.78;
          margin: 0.1em 0.12em 0 0;
        }

        .intro-section p {
          font-size: 1.08rem;
          color: #3a3a3a;
          margin-bottom: 1.3rem;
        }

        /* ── Sensitive context notice ── */
        .context-note {
          background: #EFE9DF;
          border-left: 3px solid #A8895E;
          padding: 1.1rem 1.5rem;
          margin: 1.5rem 0;
          border-radius: 0 4px 4px 0;
          font-size: 0.92rem;
          color: #5a5248;
          font-style: italic;
        }

        /* ── Source citation ── */
        .source-note {
          font-size: 0.8rem;
          color: #999;
          margin: -0.5rem 0 2rem;
        }

        .source-note a {
          color: #8a7559;
          text-decoration: underline;
        }

        /* ── Body sections ── */
        .body-section {
          padding: 0.4rem 0 1.4rem;
          opacity: 0;
          transform: translateY(22px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }

        .body-section.visible { opacity: 1; transform: none; }

        .body-section p {
          font-size: 1.04rem;
          color: #3a3a3a;
          margin-bottom: 1.3rem;
          line-height: 1.85;
        }

        .body-section p a {
          color: #5C6B5D;
          font-weight: 600;
          text-decoration: none;
          border-bottom: 1px solid #C9A876;
        }

        .section-heading {
          font-family: 'Playfair Display', serif;
          font-size: 1.5rem;
          font-weight: 600;
          color: #5C6B5D;
          margin: 0.4rem 0 1rem;
          line-height: 1.3;
        }

        /* ── Caregiver litany (the "sometimes it is..." lines) ── */
        .litany {
          font-style: italic;
          color: #57544c;
          font-size: 1.02rem;
          margin: 1.2rem 0 1.6rem;
          padding-left: 1.2rem;
          border-left: 2px solid #D8CDB8;
        }

        .litany div { margin-bottom: 0.45rem; }

        /* ── Gold divider ── */
        .gold-divider {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.8rem 0;
          opacity: 0;
          transform: scaleX(0.5);
          transform-origin: left;
          transition: opacity 0.8s ease, transform 0.8s ease;
        }

        .gold-divider.visible { opacity: 1; transform: scaleX(1); }

        .gold-divider::before, .gold-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #C9A876;
          opacity: 0.5;
        }

        .gold-star { color: #C9A876; font-size: 0.95rem; letter-spacing: 0.4em; }

        /* ── Pull quote ── */
        .pull-quote {
          border-left: 3px solid #C9A876;
          padding: 1.3rem 1.9rem;
          background: #F1ECE2;
          margin: 1.4rem 0;
          border-radius: 0 4px 4px 0;
          opacity: 0;
          transform: translateX(-18px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }

        .pull-quote.visible { opacity: 1; transform: none; }

        .pull-quote p {
          font-family: 'Playfair Display', serif;
          font-style: italic;
          font-size: 1.2rem;
          color: #4d5b4e;
          line-height: 1.6;
        }

        /* ── Inline images ── */
        .inline-image-wrap {
          margin: 1.9rem 0;
          opacity: 0;
          transform: translateY(18px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }

        .inline-image-wrap.visible { opacity: 1; transform: none; }

        .inline-image-wrap img {
          width: 100%;
          height: 500px;
          object-fit: contain;
          border-radius: 4px;
          display: block;
          filter: saturate(0.92);
        }

        .inline-image-wrap figcaption {
          font-size: 0.78rem;
          color: #999;
          letter-spacing: 0.03em;
          margin-top: 0.55rem;
          text-align: center;
          font-style: italic;
        }

        /* ── Wide image ── */
        .wide-image-wrap {
          margin: 2.3rem 0;
          opacity: 0;
          transition: opacity 0.9s ease;
        }

        .wide-image-wrap.visible { opacity: 1; }

        .wide-image-wrap img {
          width: 100%;
          height: 500px;
          object-fit: contain;
          display: block;
          filter: saturate(0.92);
        }

        .wide-image-wrap figcaption {
          text-align: center;
          font-size: 0.78rem;
          color: #999;
          font-style: italic;
          padding: 0.55rem 0 0;
          max-width: 600px;
          margin: 0 auto;
        }

        /* ── Program cards ── */
        .program-cards {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.2rem;
          margin: 1.8rem 0;
        }

        .program-card {
          background: #fff;
          border: 1px solid #EAE2D4;
          border-radius: 6px;
          padding: 1.6rem;
          opacity: 0;
          transform: translateY(18px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }

        .program-card.visible { opacity: 1; transform: none; }

        .program-card h3 {
          font-family: 'Playfair Display', serif;
          color: #5C6B5D;
          font-size: 1.1rem;
          margin-bottom: 0.6rem;
        }

        .program-card p {
          font-size: 0.92rem;
          color: #555;
          line-height: 1.65;
        }

        .program-card a {
          color: #A8895E;
          font-weight: 600;
          font-size: 0.85rem;
          text-decoration: none;
          display: inline-block;
          margin-top: 0.7rem;
        }

        /* ── Call to action list ── */
        .cta-list {
          margin: 1.4rem 0;
        }

        .cta-list .cta-item {
          display: flex;
          gap: 0.8rem;
          margin-bottom: 1rem;
          align-items: baseline;
        }

        .cta-item .cta-marker {
          color: #A8895E;
          font-family: 'Playfair Display', serif;
          font-style: italic;
          font-weight: 600;
          flex-shrink: 0;
        }

        .cta-item p { margin-bottom: 0 !important; }

        /* ── Closing card ── */
        .closing-card {
          background: #5C6B5D;
          color: #fff;
          border-radius: 6px;
          padding: 2.8rem 3.2rem;
          margin: 2.8rem 0;
          text-align: center;
          opacity: 0;
          transform: translateY(22px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }

        .closing-card.visible { opacity: 1; transform: none; }

        .closing-card h2 {
          font-family: 'Playfair Display', serif;
          font-size: 1.6rem;
          margin-bottom: 0.7rem;
          color: #fff;
        }

        .closing-card p {
          font-size: 0.95rem;
          color: rgba(255,255,255,0.82);
          margin-bottom: 0.4rem;
        }

        .closing-card a {
          color: #E8D9B8;
          text-decoration: none;
          font-weight: 600;
        }

        /* ── Resource note (wellbeing) ── */
        .resource-note {
          font-size: 0.82rem;
          color: #8a8a8a;
          text-align: center;
          margin: 1.5rem 0 0.5rem;
          font-style: italic;
        }

        /* ── Footer ── */
        .blog-footer {
          grid-column: 1 / -1;
          background: #232b25;
          color: rgba(255,255,255,0.45);
          text-align: center;
          padding: 1.7rem;
          font-size: 0.78rem;
          letter-spacing: 0.05em;
        }

        @media (max-width: 640px) {
          .hero { height: 70vh; }
          .hero-content { padding: 0 1.4rem 2.5rem; }
          .inline-image-wrap img { height: 220px; }
          .wide-image-wrap img { height: 250px; }
          .closing-card { padding: 2rem 1.4rem; }
          .program-cards { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="blog-wrap">
        {/* ── Hero ── */}
        <section className="hero">
          <img src={images.hero} alt="A reflective moment, hands held in support" />
          <div className="hero-overlay" />
          <div className="hero-content">
            <span className="hero-tag">Reflection · Caregiver Support</span>
            <h1>When the Weight Becomes Too Heavy: Why Autism Care Must Embrace the Whole Family</h1>
            <div className="hero-meta">Ray of Hope Center for Autism · Hosur & Bangalore</div>
          </div>
        </section>

        <main className="article-layout">

          {/* Intro */}
          <div className="intro-section">
            <p>
              On a quiet street in Mosman Park, Western Australia, a tragedy unfolded that left an entire community shaken.
            </p>
            <p>
              In early February 2026, emergency services discovered a family of four who had died at home, in what authorities believe was a murder-suicide. The two sons are believed to have lived with autism, requiring constant care. This is not a story we share for sensation, and not one we share to judge. It is a story that asks us to pause — to look beyond headlines and ask: <em>how heavy must the weight have been?</em>
            </p>
          </div>

          <div className="context-note">
            We share this reflection with care. Our intent is not to dwell on tragedy, but to draw attention to a need that affects caregiving families everywhere — including right here in India.
          </div>

          <p className="source-note">
            Source: <a href="https://www.abc.net.au/news/2026-02-02/mosman-park-murder-suicide-reaction-disability-support/106291002" target="_blank" rel="noopener noreferrer">ABC News</a>
          </p>

          <div className="body-section" ref={addRef}>
            <p>
              Australia has a structured disability support system in place — the National Disability Insurance Scheme (NDIS), administered by the National Disability Insurance Agency (NDIA). It is considered one of the more developed national frameworks for supporting individuals with additional needs and their families. And yet, even within such a system, a family reached a point of unbearable despair.
            </p>
            <p>
              If such a breaking point can exist in a country with institutional support structures, what about families in India — where awareness is still growing, where stigma lingers, where systems are evolving, and where much of the responsibility rests quietly on the shoulders of families?
            </p>
          </div>

          {/* Divider */}
          <div className="gold-divider" ref={addRef}><span className="gold-star">✦ ✦ ✦</span></div>

          {/* Invisible weight */}
          <div className="body-section" ref={addRef}>
            <h2 className="section-heading">The Invisible Weight Caregivers Carry</h2>
            <p>
              When we speak about autism, we often speak about therapies, milestones, diagnoses, early intervention, communication skills, sensory processing, behaviour plans. But behind every therapy session is a caregiver.
            </p>
            <div className="litany">
              <div>Sometimes it is a mother.</div>
              <div>Sometimes it is a father.</div>
              <div>Sometimes it is a grandparent who has stepped back into active parenting.</div>
              <div>Sometimes it is a sibling who grows up faster than their years.</div>
              <div>Sometimes it is an aunt, an uncle, a relative, or even a close friend.</div>
              <div>Sometimes it is a therapist or special educator who has chosen this work as a calling.</div>
            </div>
            <p>Caregiving is not a role confined to one person. It is a circle. And that circle can grow tired.</p>
          </div>

          <figure className="inline-image-wrap" ref={addRef}>
            <img src={images.caregiver} alt="A caregiver in a quiet, reflective moment" />
            <figcaption>Caregiving is a circle — and that circle can grow tired</figcaption>
          </figure>

          <div className="body-section" ref={addRef}>
            <p>
              The sleepless nights. The financial strain of therapies. The constant vigilance in public spaces. The questions from neighbours. The comparisons at family gatherings. The fear of the future. The silent wondering — <em>who will care for my child when I no longer can?</em>
            </p>
            <p>
              In India, many families navigate this journey without structured state-level financial frameworks like the NDIS. While policies and certifications exist, the lived reality for most families includes long waiting periods, limited access to specialized services, social misunderstanding, and emotional isolation.
            </p>
            <p>
              Too often, the child becomes the sole focus — and rightly so, in many ways. But somewhere along the way, the caregiver is expected to simply endure. To "be strong." To "have faith." To "manage." But strength without support can slowly erode. Faith without fellowship can feel lonely. Management without rest becomes exhaustion. And exhaustion, if left unseen, can become despair.
            </p>
          </div>

          {/* Pull Quote 1 */}
          <blockquote className="pull-quote" ref={addRef}>
            <p>"{pullQuotes[0]}"</p>
          </blockquote>

          <figure className="wide-image-wrap full-bleed" ref={addRef}>
            <img src={images.family} alt="A family supporting one another" />
            <figcaption>Autism care must embrace not just the child, but the whole family</figcaption>
          </figure>

          {/* Two journeys */}
          <div className="body-section" ref={addRef}>
            <h2 className="section-heading">The Child's Journey — and the Caregiver's Journey</h2>
            <p>
              Children on the autism spectrum need structured, evidence-based, individualized intervention. They need support that recognizes their unique communication styles, sensory experiences, and learning patterns — including Applied Behavioral Analysis (ABA), Speech Therapy, Occupational Therapy, and Special Education. These are not luxuries. They are lifelines.
            </p>
            <p>
              But parallel to the child's journey is another journey — quieter, less visible, but equally demanding: the caregiver's journey.
            </p>
            <p>Caregivers need:</p>
            <div className="litany">
              <div>Emotional processing.</div>
              <div>Safe spaces to express fear and frustration without guilt.</div>
              <div>Practical strategies for daily challenges.</div>
              <div>Reassurance that they are not failing.</div>
              <div>Community. Respite. Hope.</div>
            </div>
            <p>
              Without these, the system becomes unbalanced. The tragedy in Western Australia is heartbreaking. It should never have come to that. But it reminds us of something essential: love alone is not always enough when the weight is carried alone.
            </p>
          </div>

          {/* Divider */}
          <div className="gold-divider" ref={addRef}><span className="gold-star">✦ ✦ ✦</span></div>

          {/* Circles of support */}
          <div className="body-section" ref={addRef}>
            <h2 className="section-heading">In India, We Must Build Circles of Support</h2>
            <p>
              At <a href="https://rohautism.org/" target="_blank" rel="noopener noreferrer">Ray of Hope Center for Autism</a>, with centers in Hosur (Tamil Nadu) and Electronic City, Bangalore (Karnataka), we deeply believe that autism care must embrace the whole family. Not just the child. Not just the diagnosis. The whole ecosystem.
            </p>
            <p>
              Yes, we provide structured, evidence-based programs for children — ABA, Speech Therapy, Occupational Therapy, and Special Education — tailored to each child's individual needs. But we also recognize a truth that is often overlooked: if caregivers are emotionally depleted, progress cannot be sustained.
            </p>
            <p>That is why Ray of Hope has intentionally developed:</p>
          </div>

          {/* Program cards */}
          <div className="program-cards">
            <div className="program-card" ref={addRef}>
              <h3>Parent Counselling Programs</h3>
              <p>A confidential, compassionate space where parents can process emotions, confront burnout, navigate anxiety, and rediscover resilience.</p>
              <a href="https://rohautism.org/parent-counselling-program" target="_blank" rel="noopener noreferrer">Learn more →</a>
            </div>
            <div className="program-card" ref={addRef}>
              <h3>Caregiver Coaching Programs</h3>
              <p>Equipping grandparents, siblings, extended family, and other caregivers with practical strategies and emotional support — so the child experiences stability across environments.</p>
              <a href="https://rohautism.org/caregiver-coaching-program" target="_blank" rel="noopener noreferrer">Learn more →</a>
            </div>
          </div>

          {/* Pull Quote 2 */}
          <blockquote className="pull-quote" ref={addRef}>
            <p>"{pullQuotes[1]}"</p>
          </blockquote>

          <div className="body-section" ref={addRef}>
            <p>
              Because when we strengthen caregivers, we strengthen children. When we strengthen families, we prevent isolation. When we prevent isolation, we reduce the risk of despair.
            </p>
          </div>

          <figure className="inline-image-wrap" ref={addRef}>
            <img src={images.support} alt="Hands joined in support and solidarity" />
            <figcaption>Shared burdens, carried together — the heart of a circle of support</figcaption>
          </figure>

          {/* Does not have to end in tragedy */}
          <div className="body-section" ref={addRef}>
            <h2 className="section-heading">It Does Not Have to End in Tragedy</h2>
            <p>
              What happened in Mosman Park is devastating. A family is gone. A community grieves. Questions remain. But we must not let the story end there. We must let it become a reminder.
            </p>
            <div className="litany">
              <div>A reminder that caregiving is sacred work — and sacred work requires shared responsibility.</div>
              <div>A reminder that mental health support for caregivers is not optional — it is essential.</div>
              <div>A reminder that awareness without accessible care is incomplete.</div>
              <div>A reminder that in India, as systems continue to grow, centers of excellence must also become centers of compassion.</div>
            </div>
          </div>

          <figure className="wide-image-wrap full-bleed" ref={addRef}>
            <img src={images.hope} alt="Light breaking through, a symbol of hope" />
            <figcaption>Even when the road feels impossibly steep, there is always a path forward</figcaption>
          </figure>

          {/* Hope section */}
          <div className="body-section" ref={addRef}>
            <p>
              At Ray of Hope, our work is sustained not only by clinical frameworks but by conviction — that there is always a path forward, even when the road feels impossibly steep. We believe there is always a ray of hope.
            </p>
            <div className="litany">
              <div>Hope in early intervention.</div>
              <div>Hope in incremental progress.</div>
              <div>Hope in community.</div>
              <div>Hope in prayer.</div>
              <div>Hope in professional guidance.</div>
              <div>Hope in shared burdens.</div>
              <div>Hope in God's grace and strength when human capacity feels stretched thin.</div>
            </div>
            <p>
              We do not minimize the challenges families face. We walk with them through those challenges. We celebrate first words. We celebrate reduced discomfort with eye contact. We celebrate small gains that the world may overlook. We celebrate caregivers who show up again and again, even on the hardest days. And we remind them — you are not alone.
            </p>
          </div>

          {/* Pull Quote 3 */}
          <blockquote className="pull-quote" ref={addRef}>
            <p>"{pullQuotes[2]}"</p>
          </blockquote>

          <figure className="inline-image-wrap" ref={addRef}>
            <img src={images.community} alt="A community gathered together" />
            <figcaption>There can be shared burdens. There can be structured support. There can be resilience.</figcaption>
          </figure>

          {/* Call to action */}
          <div className="body-section" ref={addRef}>
            <h2 className="section-heading">A Call to See the Whole Picture</h2>
            <div className="cta-list">
              <div className="cta-item">
                <span className="cta-marker">If you are</span>
                <p>a parent, grandparent, sibling, or caregiver walking this path — seek support. Asking for help is not weakness. It is wisdom.</p>
              </div>
              <div className="cta-item">
                <span className="cta-marker">If you are</span>
                <p>part of a community — reduce stigma. Listen more than you advise.</p>
              </div>
              <div className="cta-item">
                <span className="cta-marker">If you are</span>
                <p>a policymaker — remember that child-focused intervention must be matched by caregiver-focused mental health support.</p>
              </div>
              <div className="cta-item">
                <span className="cta-marker">If you know</span>
                <p>a family navigating autism — share resources. Connect them to centers that understand both the science and the heart of this journey.</p>
              </div>
            </div>
            <p>
              The story from Australia is tragic. But it does not define the future of autism care. In India, in Hosur, in Bangalore, in every home where a child on the spectrum lives and grows — there can be light. There can be shared burdens. There can be structured support. There can be resilience. And above all, there can always be a ray of hope.
            </p>
          </div>

          {/* Closing card */}
          <div className="closing-card" ref={addRef}>
            <h2>You Are Not Alone</h2>
            <p>Ray of Hope Center for Autism · Hosur, Tamil Nadu & Electronic City, Bangalore</p>
            <p style={{ marginTop: "1rem" }}>
              <a href="https://rohautism.org/parent-counselling-program" target="_blank" rel="noopener noreferrer">Parent Counselling Program</a>
              &nbsp;&nbsp;·&nbsp;&nbsp;
              <a href="https://rohautism.org/caregiver-coaching-program" target="_blank" rel="noopener noreferrer">Caregiver Coaching Program</a>
            </p>
          </div>

          <p className="resource-note">
            If you or someone you know is struggling with caregiver burnout, anxiety, or thoughts of self-harm, please reach out to a mental health professional or a trusted support line in your area. You don't have to carry this alone.
          </p>

          <footer className="blog-footer">
            © 2026 Ray of Hope Center for Autism · All rights reserved
          </footer>

        </main>
      </div>
    </>
  );
}
